// src/components/Hero.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useApi from '../hooks/useApi';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function Hero() {
    const { request } = useApi();
    const navigate = useNavigate();
    const today = new Date().toISOString().split('T')[0];
    const maxDate = '2100-12-31';

    const [province, setProvince] = useState('');
    const [district, setDistrict] = useState('');
    const [checkin, setCheckin] = useState(today);
    const [checkout, setCheckout] = useState('');
    const [error, setError] = useState({ checkin: '', checkout: '' });
    const [isLoaded, setIsLoaded] = useState(false);
    const [cities, setCities] = useState([]);
    const [districts, setDistricts] = useState([]);

    const isAuth = localStorage.getItem('auth') === 'true';

    useEffect(() => {
        const fetchCities = async () => {
            try {
                const res = await request('/cities');
                setCities(res.data); // ✅ sadece veriyi al
            } catch (err) {
                console.error("Şehir verileri alınamadı:", err.message);
            }
        };
        fetchCities();
    }, []);

    useEffect(() => {
        setIsLoaded(true);
    }, []);

    const handleProvinceChange = async (e) => {
        const selectedCityId = e.target.value;
        const selectedCityObj = cities.find((city) => city.id === parseInt(selectedCityId));

        setProvince(selectedCityObj?.name || '');
        setDistrict('');
        try {
            const res = await request(`/towns/city/${selectedCityId}`);
            setDistricts(res.data || []);
        } catch (err) {
            console.error("İlçeler alınamadı:", err.message);
            setDistricts([]);
        }
    };

    const handleDateChange = (name) => (e) => {
        const val = e.target.value;
        if (val && (val < today || val > maxDate)) {
            setError((prev) => ({
                ...prev,
                [name]: `Lütfen ${today} - ${maxDate} arası tarih seçin.`,
            }));
        } else {
            setError((prev) => ({ ...prev, [name]: '' }));
            name === 'checkin' ? setCheckin(val) : setCheckout(val);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isAuth) {
            toast.error('Lütfen önce giriş yapınız.');
            navigate('/login');
            return;
        }

        try {
            const response = await request('/accommodations/available', 'POST', {
                startDate: checkin,
                endDate: checkout,
                city: province,
                town: district,
            });
            const matched = response.data;
            if (!matched || matched.length === 0) {
                toast.warn('Seçtiğiniz kriterlere uygun pansiyon bulunamadı.');
                return;
            }

            navigate('/search', {
                state: {
                    results: matched,
                    filters: { province, district, checkin, checkout },
                },
            });
        } catch (err) {
            console.error(err);
            toast.error('Arama sırasında bir hata oluştu.');
        }
    };

    return (
        <section className="relative w-full h-screen overflow-hidden">
            <div className="absolute inset-0 w-full h-full overflow-hidden">
                <img
                    src="https://cdn.pixabay.com/photo/2020/06/25/10/21/architecture-5339245_1280.jpg"
                    alt="Pansiyon"
                    className={`w-full h-full object-cover transition-transform duration-1000 ${isLoaded ? 'scale-100' : 'scale-110'}`}
                />
                <div className={`absolute inset-0 bg-gradient-to-b from-black/30 to-black/60 transition-opacity duration-1000 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}></div>
            </div>

            <div className={`relative z-10 flex flex-col items-center justify-center h-full transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <div className="max-w-3xl text-center px-4 mb-12 animate-bounce-slow">
                    <h2 style={{ fontFamily: '"Hanalei Fill", cursive' }} className="text-white text-5xl font-bold leading-tight drop-shadow-lg">
                        sıcak havalarda<br />pansiyon bulmak<br />artık kolay
                    </h2>
                </div>

                <form
                    onSubmit={handleSubmit}
                    className={`grid grid-cols-1 md:grid-cols-2 gap-6 bg-white bg-opacity-20 backdrop-blur-md rounded-2xl p-8 shadow-2xl max-w-4xl w-full mx-4 transition-all duration-700 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                >
                    <div className="flex flex-col">
                        <label className="text-sm font-semibold text-white uppercase mb-2">İl</label>
                        <select
                            value={province}
                            onChange={handleProvinceChange}
                            className="p-3 rounded bg-white bg-opacity-70 hover:bg-opacity-90 transition-all duration-300"
                            required
                        >
                            <option value="" disabled>Şehir seçiniz</option>
                            {cities.map((city) => (
                                <option key={city.id} value={city.id}>{city.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex flex-col">
                        <label className="text-sm font-semibold text-white uppercase mb-2">İlçe</label>
                        <select
                            value={district}
                            onChange={(e) => setDistrict(e.target.value)}
                            className="p-3 rounded bg-white bg-opacity-70 hover:bg-opacity-90 transition-all duration-300"
                            disabled={districts.length === 0}
                            required
                        >
                            <option value="" disabled>İlçe seçiniz</option>
                            {districts.map((ilce) => (
                                <option key={ilce.id} value={ilce.name}>{ilce.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className="flex flex-col">
                        <label className="text-sm font-semibold text-white uppercase mb-2">Giriş Tarihi</label>
                        <input
                            type="date"
                            value={checkin}
                            min={today}
                            max={maxDate}
                            onChange={handleDateChange('checkin')}
                            className="p-3 rounded bg-white bg-opacity-70 hover:bg-opacity-90 transition-all duration-300"
                            required
                        />
                    </div>

                    <div className="flex flex-col">
                        <label className="text-sm font-semibold text-white uppercase mb-2">Çıkış Tarihi</label>
                        <input
                            type="date"
                            value={checkout}
                            min={checkin}
                            max={maxDate}
                            onChange={handleDateChange('checkout')}
                            className="p-3 rounded bg-white bg-opacity-70 hover:bg-opacity-90 transition-all duration-300"
                            required
                        />
                    </div>

                    <button
                        type="submit"
                        className="col-span-full mt-4 py-4 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold text-lg shadow-lg hover:shadow-xl hover:from-indigo-600 hover:to-purple-700 transition-all duration-500 hover:scale-105 transform animate-pulse hover:animate-none"
                    >
                        Pansiyon Ara
                    </button>
                </form>
            </div>

            <ToastContainer position="top-right" autoClose={3000} />

            <style jsx>{`
                @keyframes bounce-slow {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-20px); }
                }
                .animate-bounce-slow {
                    animation: bounce-slow 3s ease-in-out infinite;
                }
                .animate-pulse {
                    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
                }
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.8; }
                }
            `}</style>
        </section>
    );
}
